import {
    useContext
} from "react";
import type {
    ChartSeriesColor,
    ChartColorIndex,
    ChartPattern
} from "./type";
import {
    ChartContext
} from "./context";
import useChartSeriesFade from "./useChartSeriesFade";
import {
    getChartSeriesColor,
    getChartPattern
} from "./util";
import {
    NCoreUIKitTheme
} from "../../core/hooks";

export type ChartSeriesItem = ChartSeriesColor & {
    colorIndex: ChartColorIndex;
    pattern: ChartPattern;
    isHidden: boolean;
    opacity: number;
    title: string;
    key: string;
};

const useChartSeries = ({
    seriesKeys
}: {
    seriesKeys: Array<string>;
}): {
    visibleSeries: Array<ChartSeriesItem>;
    series: Array<ChartSeriesItem>;
    patternID: string;
} => {
    const context = useContext(ChartContext);

    const {
        colors
    } = NCoreUIKitTheme.useContext(context?.customTheme);

    const hiddenKeys = context?.hiddenKeys ?? [];

    const opacities = useChartSeriesFade({
        customTheme: context?.customTheme,
        hiddenKeys,
        seriesKeys
    });

    const series = seriesKeys.map((key, index): ChartSeriesItem => {
        const item = context?.config[key];

        const colorIndex: ChartColorIndex = item?.colorIndex ?? ((index % 8) + 1 as ChartColorIndex);

        return {
            ...getChartSeriesColor({
                colorIndex,
                colors
            }),
            pattern: getChartPattern({
                isPatterned: context?.isPatterned ?? false,
                colorIndex
            }),
            isHidden: hiddenKeys.includes(key),
            opacity: opacities[key] ?? 1,
            title: item?.title ?? key,
            colorIndex,
            key
        };
    });

    return {
        visibleSeries: series.filter((item) => !item.isHidden || item.opacity > 0),
        patternID: `${context?.baseID ?? "chart"}-pattern`,
        series
    };
};
export default useChartSeries;
